import { useEffect, useState } from "react";
import {
  BarChart3, Copy, Edit3, FileText, Loader2, Mail, Plus, Save, Search, Star, Trash2, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const CATEGORIES = ["Company News", "Shipping Tips", "International Shipping", "Domestic Courier", "NRI Shipping",
  "eCommerce", "Business Logistics", "Packing Guides", "Events", "Achievements", "Announcements", "Industry Insights"];
const STATUS_META = { draft: ["Draft", "bg-amber-500/15 text-amber-400"], scheduled: ["Scheduled", "bg-blue-500/15 text-blue-400"], published: ["Published", "bg-emerald-500/15 text-emerald-400"], archived: ["Archived", "bg-slate-500/15 text-slate-400"] };
const emptyArticle = { title: "", slug: "", category: "Company News", tags: "", excerpt: "", content: "",
  image_path: "", card_path: "", thumb_path: "", focal_x: 50, focal_y: 50, author: "IndiaFast Team",
  published_date: "", updated_date: "", reading_time: 3, featured: false, editors_pick: false,
  status: "draft", scheduled_at: "", seo_title: "", meta_description: "", og_image: "", allow_indexing: true };
const FOCAL_PRESETS = [["Top", 50, 15], ["Center", 50, 50], ["Bottom", 50, 85], ["Left", 15, 50], ["Right", 85, 50]];

export default function AdminNews() {
  const [tab, setTab] = useState("articles");
  const [data, setData] = useState({ items: [], metrics: {} });
  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ status: "", category: "", q: "" });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/news/admin/articles?${p}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, [filters]);
  useEffect(() => { if (tab === "subscribers") api.get("/news/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, [tab]);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/mediahub/admin/upload-image", fd);
      setForm((f) => ({ ...f, image_path: r.data.path, card_path: r.data.card_path, thumb_path: r.data.thumb_path }));
      toast.success("Image optimized & uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags: String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
                        reading_time: parseInt(form.reading_time) || 3 };
      delete payload.id; delete payload.views; delete payload.created_at; delete payload.updated_at;
      if (form.id) await api.put(`/news/admin/articles/${form.id}`, payload);
      else await api.post("/news/admin/articles", payload);
      toast.success(form.id ? "Article updated" : "Article created");
      setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    try { await api.delete(`/news/admin/articles/${a.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const m = data.metrics || {};

  return (
    <div data-testid="admin-news">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">News &amp; Blog</h1>
          <p className="mt-1 text-sm text-slate-500">Articles published here appear on the News &amp; Insights page.</p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1.5">
            {[["articles", "Articles", FileText], ["subscribers", "Subscribers", Mail]].map(([k, l, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`news-tab-${k}`}
                      className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold ${tab === k ? "bg-saffron text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                <Icon className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>
          {tab === "articles" && (
            <button onClick={() => setForm({ ...emptyArticle })} data-testid="news-new-article-btn"
                    className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
              <Plus className="h-3.5 w-3.5" /> New Article
            </button>
          )}
        </div>
      </div>

      {tab === "subscribers" && (
        <div className="mt-5 space-y-2.5" data-testid="news-subscribers">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-xl border border-adminborder bg-admincard p-4" data-testid={`news-sub-${s.id}`}>
              <div><p className="text-sm font-bold text-white">{s.name}</p><p className="text-xs text-slate-400">{s.email}</p></div>
              <span className="text-[10px] text-slate-500">{(s.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
          {!subs.length && <div className="rounded-xl border border-adminborder bg-admincard p-12 text-center text-sm text-slate-500">No subscribers yet.</div>}
        </div>
      )}

      {tab === "articles" && (
        <>
          {/* METRICS */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="news-metrics">
            {[["Total", m.total ?? 0], ["Published", m.by_status?.published ?? 0], ["Drafts", m.by_status?.draft ?? 0],
              ["Scheduled", m.by_status?.scheduled ?? 0], ["Total Views", m.total_views ?? 0]].map(([l, v], i) => (
              <div key={l} className="rounded-xl border border-adminborder bg-admincard p-3.5 text-center" data-testid={`news-metric-${i}`}>
                <p className="font-heading text-xl font-extrabold text-white">{v}</p>
                <p className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">{l}</p>
              </div>
            ))}
            <div className="rounded-xl border border-adminborder bg-admincard p-3.5 text-center" data-testid="news-metric-top">
              <p className="flex items-center justify-center gap-1 font-heading text-xs font-extrabold text-saffron"><BarChart3 className="h-3.5 w-3.5" />{m.top_category || "—"}</p>
              <p className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">Top Category</p>
            </div>
          </div>
          {m.top_article && <p className="mt-3 text-[11px] text-slate-400">Most read: <span className="font-bold text-slate-200">{m.top_article}</span></p>}

          {/* FILTERS */}
          <div className="mt-5 flex flex-wrap gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3">
              <Search className="h-3.5 w-3.5 text-slate-500" />
              <input className="bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search articles…" data-testid="news-search"
                     value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            </div>
            <select className={`${darkInput} max-w-36`} data-testid="news-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Status</option>{Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s][0]}</option>)}
            </select>
            <select className={`${darkInput} max-w-48`} data-testid="news-filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
              <option value="">All Categories</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* TABLE */}
          <div className="mt-4 overflow-x-auto rounded-xl border border-adminborder bg-admincard">
            <table className="w-full text-sm" data-testid="news-table">
              <thead>
                <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Article</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Views</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`news-row-${a.id}`}>
                    <td className="max-w-72 px-4 py-3">
                      <p className="line-clamp-1 font-semibold text-white">{a.title}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-slate-500">/p/news/{a.slug}</p>
                      <div className="mt-1 flex gap-1">
                        {a.featured && <span className="rounded bg-saffron/15 px-1.5 py-0.5 text-[9px] font-bold text-saffron">FEATURED</span>}
                        {a.editors_pick && <span className="rounded bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-bold text-purple-400">EDITOR'S PICK</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{a.category}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_META[a.status]?.[1]}`}>{STATUS_META[a.status]?.[0] || a.status}</span></td>
                    <td className="px-4 py-3 text-xs font-bold text-slate-300">{a.views ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{(a.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <a href={`/p/news/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`news-preview-${a.id}`}
                           className="rounded-lg border border-adminborder px-2 py-1.5 text-[10px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">Preview</a>
                        <button onClick={() => setForm({ ...a, tags: (a.tags || []).join(", ") })} data-testid={`news-edit-${a.id}`}
                                className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-saffron hover:text-saffron"><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={async () => { await api.post(`/news/admin/articles/${a.id}/duplicate`); toast.success("Duplicated as draft"); load(); }}
                                data-testid={`news-dup-${a.id}`} className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-blue-400 hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(a)} data-testid={`news-delete-${a.id}`} className="rounded-lg bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!data.items.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No articles yet — create your first story.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ARTICLE FORM MODAL */}
      {form && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="news-form-modal">
          <div className="mx-auto my-8 w-full max-w-3xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-white">{form.id ? "Edit Article" : "New Article"}</h3>
              <button onClick={() => setForm(null)} className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input className={`${darkInput} sm:col-span-2`} data-testid="article-form-title" placeholder="Article Title *" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} />
              <input className={darkInput} data-testid="article-form-slug" placeholder="URL slug (auto from title)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              <select className={darkInput} data-testid="article-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className={`${darkInput} sm:col-span-2`} data-testid="article-form-tags" placeholder="Tags, comma separated" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              <textarea rows={2} className={`${darkInput} sm:col-span-2`} data-testid="article-form-excerpt" placeholder="Short description / excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              <div className="sm:col-span-2">
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-adminborder bg-adminbg px-4 py-4 text-xs font-bold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="article-form-image">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {form.image_path ? "Featured image uploaded — click to replace" : "Upload featured image (auto WebP card + thumb)"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} />
                </label>
                {form.image_path && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] text-slate-500">Focal:</span>
                    {FOCAL_PRESETS.map(([l, x, y]) => (
                      <button type="button" key={l} onClick={() => setForm({ ...form, focal_x: x, focal_y: y })} data-testid={`article-focal-${l.toLowerCase()}`}
                              className={`rounded-full px-3 py-1 text-[10px] font-bold ${form.focal_x === x && form.focal_y === y ? "bg-saffron text-white" : "bg-white/5 text-slate-400"}`}>{l}</button>
                    ))}
                  </div>
                )}
              </div>
              <textarea rows={10} className={`${darkInput} font-mono text-xs sm:col-span-2`} data-testid="article-form-content"
                        placeholder={"Article content. Formatting:\n## Heading\n- bullet\n1. numbered\n> quote\n**bold**\n![alt](image-path|caption)\n[video](youtube-url)"}
                        value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              <input className={darkInput} data-testid="article-form-author" placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
              <input type="number" min="1" className={darkInput} data-testid="article-form-reading" placeholder="Reading time (min)" value={form.reading_time} onChange={(e) => setForm({ ...form, reading_time: e.target.value })} />
              <div><label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Published date</label>
                <input type="date" className={darkInput} data-testid="article-form-pubdate" value={form.published_date} onChange={(e) => setForm({ ...form, published_date: e.target.value })} /></div>
              <div><label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Updated date</label>
                <input type="date" className={darkInput} data-testid="article-form-upddate" value={form.updated_date} onChange={(e) => setForm({ ...form, updated_date: e.target.value })} /></div>
              <select className={darkInput} data-testid="article-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="published">Published</option><option value="archived">Archived</option>
              </select>
              {form.status === "scheduled" && (
                <div><label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Publish at</label>
                  <input type="datetime-local" className={darkInput} data-testid="article-form-schedule" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></div>
              )}
              <div className="flex flex-wrap gap-4 sm:col-span-2">
                {[["featured", "Featured story"], ["editors_pick", "Editor's Pick"], ["allow_indexing", "Allow search indexing"]].map(([k, l]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-300" data-testid={`article-form-${k}`}>
                    <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="h-4 w-4 accent-saffron" /> {l}
                  </label>
                ))}
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:col-span-2">SEO</p>
              <input className={darkInput} data-testid="article-form-seo-title" placeholder="SEO title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} />
              <input className={darkInput} data-testid="article-form-og-image" placeholder="OG image path (optional)" value={form.og_image} onChange={(e) => setForm({ ...form, og_image: e.target.value })} />
              <textarea rows={2} className={`${darkInput} sm:col-span-2`} data-testid="article-form-meta-desc" placeholder="Meta description" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setForm(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="article-form-save"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {form.id ? "Save Changes" : "Create Article"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
